import React, { useCallback, useEffect, useRef, useState } from "react";
import { FolderClosed, Upload, CheckCircle2, Loader2, FileText, Clock, CircleDashed, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VideoRecorder from "./VideoRecorder";
import { useTranslation } from "react-i18next";

const STATUS = {
  pending: { t: "client.payPending", c: "bg-amber-100 text-amber-700 border-amber-200", Icon: Clock },
  partial: { t: "client.docPartial", c: "bg-blue-100 text-blue-700 border-blue-200", Icon: CircleDashed },
  completed: { t: "client.docCompleted", c: "bg-green-100 text-green-700 border-green-200", Icon: CheckCircle2 },
};

export default function MyDocuments() {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [recDoc, setRecDoc] = useState(null);
  const fileRef = useRef(null);
  const target = useRef({ token: null, docId: null });

  const load = useCallback(async () => {
    try { const r = await api.get("/my/upload-links"); setLinks(r.data.links || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pick = (token, docId, video) => {
    target.current = { token, docId };
    if (fileRef.current) fileRef.current.accept = video ? "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" : ".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,application/pdf,image/*";
    fileRef.current?.click();
  };

  const uploadFile = async (token, docId, file) => {
    setUploading(docId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/upload-link/${token}/documents/${docId}`, fd);
      setLinks((ls) => ls.map((l) => l.token === token ? { ...l, documents: res.data.documents, status: res.data.status, uploaded_count: res.data.documents.filter((d) => d.uploaded).length } : l));
      toast.success(t("client.docUploaded"));
    } catch (err) { toast.error(err?.response?.data?.detail || t("client.uploadFailed")); }
    finally { setUploading(null); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const { token, docId } = target.current;
    const doc = (links.find((l) => l.token === token)?.documents || []).find((x) => x.id === docId);
    const isVideo = doc?.kind === "video";
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (isVideo) {
      if (!["mp4", "webm", "mov", "m4v"].includes(ext)) return toast.error(t("client.badVideoType"));
      if (file.size > 50 * 1024 * 1024) return toast.error(t("client.videoTooLarge"));
    } else {
      const ALLOWED = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "doc", "docx"];
      if (!ALLOWED.includes(ext)) return toast.error(t("client.badFileType"));
      if (file.size > 25 * 1024 * 1024) return toast.error(t("client.fileTooLarge"));
    }
    await uploadFile(token, docId, file);
  };

  if (loading) return null;
  if (links.length === 0) return null;

  return (
    <div className="mt-10" data-testid="my-documents">
      <input type="file" ref={fileRef} onChange={onFile} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,application/pdf,image/*" className="hidden" data-testid="my-docs-file-input" />
      <div className="flex items-center gap-2 mb-4">
        <FolderClosed className="h-5 w-5 text-orange-700" />
        <h2 className="font-heading text-xl tracking-tight text-foreground">{t("client.myDocuments")}</h2>
      </div>
      <div className="space-y-4">
        {links.map((l) => {
          const s = STATUS[l.status] || STATUS.pending;
          return (
            <div key={l.token} className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`my-doc-link-${l.token}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.c}`} data-testid={`my-doc-status-${l.token}`}><s.Icon className="h-3 w-3" /> {t(s.t)}</span>
                  <span className="text-xs text-muted-foreground">{t("client.uploadedCount", { done: l.uploaded_count, total: l.doc_count })}</span>
                  {l.submitted && <span className="text-[10px] uppercase tracking-wider text-green-700 border border-green-200 bg-green-50 rounded-sm px-1.5 py-0.5">{t("client.submitted")}</span>}
                </div>
                <span className="text-xs text-muted-foreground">{l.req_id ? t("client.forOrder") : t("client.docRequest")}</span>
              </div>
              {l.message && <p className="mt-2 text-sm text-muted-foreground">{l.message}</p>}
              <div className="mt-3 space-y-2">
                {(l.documents || []).map((d) => {
                  const done = d.uploaded;
                  const busy = uploading === d.id;
                  return (
                    <div key={d.id} className={`rounded-lg border p-3 ${done ? "border-green-200 bg-green-50/50" : "border-border bg-secondary/30"}`} data-testid={`my-doc-row-${d.id}`}>
                      {d.kind === "video" && d.instructions ? (
                        <div className="mb-2 rounded-md border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-900" data-testid={`my-doc-video-instructions-${d.id}`}>
                          <p className="text-[10px] uppercase tracking-wider text-blue-700 mb-0.5">{t("client.videoInstructions")}</p>
                          {d.instructions}
                        </div>
                      ) : null}
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`grid place-items-center h-8 w-8 rounded-sm shrink-0 ${done ? "bg-green-100 text-green-600" : "bg-secondary text-muted-foreground"}`}>{done ? <CheckCircle2 className="h-4 w-4" /> : <FileText className="h-4 w-4" />}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{d.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{done ? (d.filename || t("client.uploaded")) : t("client.awaitingUpload")}</p>
                          </div>
                        </div>
                        {d.kind === "video" ? (
                          <div className="shrink-0 flex items-center gap-1.5 flex-wrap">
                            <button onClick={() => setRecDoc({ token: l.token, docId: d.id })} disabled={busy} className="inline-flex items-center rounded-sm bg-[#23488F] text-white px-3 py-1.5 text-xs hover:bg-[#1b3a75] disabled:opacity-50" data-testid={`my-doc-record-${d.id}`}><Video className="h-3.5 w-3.5 mr-1" /> {t("client.recordNow")}</button>
                            <button onClick={() => pick(l.token, d.id, true)} disabled={busy}
                              className={`inline-flex items-center rounded-sm px-3 py-1.5 text-xs transition-colors ${done ? "border border-border bg-card text-muted-foreground hover:bg-secondary" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                              data-testid={`my-doc-video-upload-${d.id}`}>
                              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (done ? t("client.replace") : <><Upload className="h-3.5 w-3.5 mr-1" /> {t("client.uploadVideo")}</>)}
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => pick(l.token, d.id)} disabled={busy}
                            className={`shrink-0 inline-flex items-center rounded-sm px-3 py-1.5 text-xs transition-colors ${done ? "border border-border bg-card text-muted-foreground hover:bg-secondary" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                            data-testid={`my-doc-upload-${d.id}`}>
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (done ? t("client.replace") : <><Upload className="h-3.5 w-3.5 mr-1" /> {t("client.upload")}</>)}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <VideoRecorder open={!!recDoc} uploading={uploading === recDoc?.docId} onClose={() => setRecDoc(null)} onConfirm={(f) => { const t = recDoc; setRecDoc(null); uploadFile(t.token, t.docId, f); }} />
    </div>
  );
}
